import React, { useState, useCallback, useEffect } from 'react';
import { Sparkles, Loader2, ChevronDown, Check, ArrowRight, Wand2, Package, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { ANGLE_LABELS, getRecommendedTemplates } from '../../data/pinTemplates';
import TemplatePicker from './TemplatePicker';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({});

const COUNT_OPTIONS = [3, 5, 10];

const PinCreator = ({ brandKit, pinterestStatus, onPinsCreated, onEditPin }) => {
  const [step, setStep] = useState('setup'); // setup | angles | generating | done
  const [productTitle, setProductTitle] = useState('');
  const [productDesc, setProductDesc] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [productKeywords, setProductKeywords] = useState('');
  const [destinationUrl, setDestinationUrl] = useState('');
  const [count, setCount] = useState(5);
  const [selectedAngles, setSelectedAngles] = useState([]);

  // Auto-fill from sessionStorage when navigating from a product
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('pinterest_studio_product');
      if (raw) {
        const data = JSON.parse(raw);
        if (data.title) setProductTitle(data.title);
        if (data.description) setProductDesc(data.description);
        if (data.category) setProductCategory(data.category);
        if (data.keywords) setProductKeywords(data.keywords);
        if (data.destinationUrl) setDestinationUrl(data.destinationUrl);
        sessionStorage.removeItem('pinterest_studio_product');
      }
    } catch {}
  }, []);
  const [generatedPins, setGeneratedPins] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');

  // Pre-select angles based on category
  const suggestAngles = useCallback((cat) => {
    const recommended = getRecommendedTemplates(cat, '', 3);
    const allAngles = Object.keys(ANGLE_LABELS);
    // Pick a balanced set
    const pool = [...allAngles];
    const shuffled = pool.sort(() => Math.random() - 0.5);
    setSelectedAngles(shuffled.slice(0, count));
    if (!selectedTemplate && recommended[0]) setSelectedTemplate(recommended[0]);
  }, [count, selectedTemplate]);

  const toggleAngle = (angle) => {
    setSelectedAngles((prev) => {
      if (prev.includes(angle)) return prev.filter((a) => a !== angle);
      if (prev.length >= count) return prev;
      return [...prev, angle];
    });
  };

  const handleGenerate = async () => {
    if (!productTitle.trim()) { setError('Add et productnavn'); return; }
    setGenerating(true);
    setError('');

    try {
      const res = await axios.post(`${API}/api/pin-studio/generate`, {
        source_product_title: productTitle,
        source_product_description: productDesc,
        source_product_category: productCategory,
        source_product_keywords: productKeywords.split(',').map((k) => k.trim()).filter(Boolean),
        count,
        angles: selectedAngles.length >= count ? selectedAngles.slice(0, count) : null,
        brand_kit: brandKit?.brand_name ? brandKit : null,
        destination_url: destinationUrl,
      }, getAuth());

      setGeneratedPins(res.data.pins || []);
      setStep('done');
    } catch (e) {
      setError(e.response?.data?.detail || 'AI-generering feilet. Try again.');
    }
    setGenerating(false);
  };

  // Step 1: Product setup
  if (step === 'setup') {
    return (
      <div className="space-y-6" data-testid="pin-creator-setup">
        <div>
          <h2 className="font-serif font-bold text-stone-800 text-xl">Create Pins med AI</h2>
          <p className="font-sans text-xs text-stone-400 mt-1">Describe your product, choose the number — AI creates unique Pins with different marketing angles.</p>
        </div>

        <div className="bg-white rounded-2xl border border-stone-100 p-6 space-y-5">
          <div>
            <label className="font-sans text-xs font-semibold text-stone-600 mb-1.5 block">Productnavn *</label>
            <input
              data-testid="product-title-input"
              value={productTitle}
              onChange={(e) => setProductTitle(e.target.value)}
              placeholder="e.g. Wedding Planner, Budget Tracker..."
              className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] transition-colors"
            />
          </div>

          <div>
            <label className="font-sans text-xs font-semibold text-stone-600 mb-1.5 block">Description</label>
            <textarea
              data-testid="product-desc-input"
              value={productDesc}
              onChange={(e) => setProductDesc(e.target.value)}
              placeholder="Short description of the product..."
              rows={2}
              className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] transition-colors resize-none"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-xs font-semibold text-stone-600 mb-1.5 block">Kategori</label>
              <input
                data-testid="product-category-input"
                value={productCategory}
                onChange={(e) => setProductCategory(e.target.value)}
                placeholder="e.g. Wedding, Finance, Health..."
                className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] transition-colors"
              />
            </div>
            <div>
              <label className="font-sans text-xs font-semibold text-stone-600 mb-1.5 block">Keywords (kommaseparert)</label>
              <input
                data-testid="product-keywords-input"
                value={productKeywords}
                onChange={(e) => setProductKeywords(e.target.value)}
                placeholder="wedding, planner, bridal..."
                className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="font-sans text-xs font-semibold text-stone-600 mb-1.5 block">Destination URL</label>
            <input
              data-testid="destination-url-input"
              value={destinationUrl}
              onChange={(e) => setDestinationUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] transition-colors"
            />
          </div>

          {/* Count selector */}
          <div>
            <label className="font-sans text-xs font-semibold text-stone-600 mb-2 block">Number of Pins</label>
            <div className="flex gap-2">
              {COUNT_OPTIONS.map((n) => (
                <button
                  key={n}
                  data-testid={`count-${n}`}
                  onClick={() => setCount(n)}
                  className={`px-5 py-2.5 rounded-xl text-sm font-sans font-semibold transition-all ${
                    count === n
                      ? 'bg-[#E60023] text-white shadow-sm'
                      : 'bg-stone-50 text-stone-600 hover:bg-stone-100'
                  }`}
                >
                  {n} Pins
                </button>
              ))}
            </div>
          </div>

          {error && <p className="font-sans text-xs text-red-500">{error}</p>}

          <Button
            data-testid="next-to-angles-btn"
            onClick={() => {
              if (!productTitle.trim()) { setError('Add et productnavn'); return; }
              setError('');
              suggestAngles(productCategory.toLowerCase().replace(/\s+/g, '-'));
              setStep('angles');
            }}
            className="w-full bg-stone-900 hover:bg-stone-800 text-white rounded-xl py-5 font-sans font-semibold"
          >
            Choose marketing angles <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // Step 2: Angle selection
  if (step === 'angles') {
    return (
      <div className="space-y-6" data-testid="pin-creator-angles">
        <div>
          <h2 className="font-serif font-bold text-stone-800 text-xl">Choose marketing angles</h2>
          <p className="font-sans text-xs text-stone-400 mt-1">
            Choose {count} angles — each Pin gets its own approach. You can change these after generation.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-stone-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="font-sans text-xs text-stone-500">
              <span className="font-semibold text-stone-700">{selectedAngles.length}</span> / {count} selected
            </p>
            <button
              onClick={() => { suggestAngles(productCategory.toLowerCase().replace(/\s+/g, '-')); }}
              className="text-[10px] font-sans font-medium text-[#B49A88] hover:underline flex items-center gap-1"
            >
              <Wand2 className="w-3 h-3" /> Suggest automatically
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-2.5">
            {Object.entries(ANGLE_LABELS).map(([key, val]) => {
              const selected = selectedAngles.includes(key);
              const disabled = !selected && selectedAngles.length >= count;
              return (
                <button
                  key={key}
                  data-testid={`angle-${key}`}
                  onClick={() => !disabled && toggleAngle(key)}
                  disabled={disabled}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all text-left ${
                    selected
                      ? 'border-[#E60023]/30 bg-[#E60023]/5'
                      : disabled
                        ? 'border-stone-100 bg-stone-50 opacity-40'
                        : 'border-stone-100 hover:border-stone-200 bg-white'
                  }`}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-sm flex-shrink-0"
                    style={{ background: val.color + '15', color: val.color }}
                  >
                    {selected ? <Check className="w-4 h-4" /> : val.emoji}
                  </div>
                  <div>
                    <p className="font-sans font-semibold text-stone-700 text-xs">{val.label}</p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Template picker */}
          <div className="mt-5 pt-5 border-t border-stone-100">
            <h4 className="font-sans font-bold text-stone-700 text-xs mb-3">Choose Pin-design (optional)</h4>
            <p className="font-sans text-[10px] text-stone-400 mb-3">Recommended based on product + marketing angle. AI uses this as a starting point.</p>
            <TemplatePicker
              productCategory={productCategory}
              marketingAngle={selectedAngles[0] || ''}
              selectedId={selectedTemplate}
              onSelect={setSelectedTemplate}
            />
          </div>

          {error && <p className="font-sans text-xs text-red-500 mt-3">{error}</p>}

          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={() => setStep('setup')} className="rounded-xl px-5 py-4 text-xs font-sans font-semibold">
              Back
            </Button>
            <Button
              data-testid="generate-pins-btn"
              onClick={handleGenerate}
              disabled={generating || selectedAngles.length === 0}
              className="flex-1 bg-[#E60023] hover:bg-[#cc001f] text-white rounded-xl py-4 font-sans font-semibold disabled:opacity-50"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> AI genererer {count} Pins...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Generer {count} Pins</>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Results
  return (
    <div className="space-y-6" data-testid="pin-creator-done">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif font-bold text-stone-800 text-xl">{generatedPins.length} Pins generert</h2>
          <p className="font-sans text-xs text-stone-400 mt-1">for {productTitle}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setStep('setup'); setGeneratedPins([]); }} className="rounded-xl px-4 py-2 text-xs font-sans font-semibold" data-testid="create-more-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Lag flere
          </Button>
          <Button onClick={onPinsCreated} className="bg-stone-900 hover:bg-stone-800 text-white rounded-xl px-4 py-2 text-xs font-sans font-semibold" data-testid="go-to-pins-btn">
            Se alle Pins <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {generatedPins.map((pin) => {
          const angleInfo = ANGLE_LABELS[pin.marketing_angle] || { label: pin.marketing_angle, color: '#888' };
          return (
            <div
              key={pin.id}
              className="bg-white rounded-2xl border border-stone-100 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all group cursor-pointer"
              onClick={() => onEditPin(pin.id)}
              data-testid={`generated-pin-${pin.id}`}
            >
              {/* Pin preview (template-based) */}
              <div className="aspect-[2/3] bg-gradient-to-b from-stone-50 to-stone-100 p-6 flex flex-col justify-center items-center text-center relative">
                <span
                  className="absolute top-3 left-3 text-[9px] font-sans font-bold px-2 py-1 rounded-full"
                  style={{ background: angleInfo.color + '15', color: angleInfo.color }}
                >
                  {angleInfo.label}
                </span>
                <p className="font-serif font-bold text-stone-800 text-lg leading-tight mb-2">{pin.headline}</p>
                <p className="font-sans text-stone-500 text-[11px] leading-relaxed mb-3">{pin.subheadline}</p>
                {pin.cta && (
                  <span className="px-4 py-1.5 bg-[#B49A88] text-white text-[10px] font-sans font-semibold rounded-full">{pin.cta}</span>
                )}
              </div>
              <div className="p-3">
                <p className="font-sans font-medium text-stone-700 text-xs truncate">{pin.pinterest_title}</p>
                <p className="font-sans text-[10px] text-stone-400 mt-0.5 truncate">{pin.pinterest_description?.slice(0, 80)}...</p>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-stone-50">
                  <span className="text-[9px] font-sans font-medium text-stone-400 uppercase">Draft</span>
                  <span className="text-[10px] font-sans text-[#E60023] font-medium group-hover:underline">Edit →</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PinCreator;
